// Online-only REST adapters for design v6 sharing features (docs/sharing-links.md, docs/project-trash.md,
// docs/auth-usernames.md). Same-origin session cookie + CSRF via api(); errors surface as TransportError
// (kind + server error code) and are mapped to UI strings by the pane controller.
import type { OnlinePort, InboxInvite, InvitationView, ShareLink, SharingInfo, TrashedProject } from '@/modules/panes/ports';
import { api } from './client';

export function httpOnlinePort(workspaceId: string): OnlinePort {
  const ws = `/workspaces/${encodeURIComponent(workspaceId)}`;
  const proj = (id: string) => `${ws}/projects/${encodeURIComponent(id)}`;
  return {
    sharing: id => api<SharingInfo>('GET', `${proj(id)}/sharing`),
    invite: async (id, invitee, role = 'editor') => {
      const r = await api<{ status: string; already_invited?: boolean; invitation?: InvitationView }>('POST', `${proj(id)}/invitations`, { invitee, role });
      return { already_invited: !!r.already_invited, invitation: r.invitation };
    },
    cancelInvite: (id, inv) => api<void>('DELETE', `${proj(id)}/invitations/${encodeURIComponent(inv)}`),
    links: async id => (await api<{ links: ShareLink[] }>('GET', `${proj(id)}/links`)).links,
    createLink: async (id, scope) => (await api<{ link: ShareLink }>('POST', `${proj(id)}/links`, scope)).link,
    revokeLink: (id, link) => api<void>('DELETE', `${proj(id)}/links/${encodeURIComponent(link)}`),
    inbox: async () => (await api<{ invites: InboxInvite[] }>('GET', '/invites')).invites,
    acceptInvite: id => api('POST', `/invites/${encodeURIComponent(id)}/accept`),
    declineInvite: async id => { await api('POST', `/invites/${encodeURIComponent(id)}/decline`); },
    trashedProjects: async () => (await api<{ projects: TrashedProject[] }>('GET', `${ws}/trash/projects`)).projects,
    purgeProject: id => api<void>('DELETE', proj(id)),
    updateProfile: async patch => (await api<{ user: { id: string; username: string | null; display_name: string } }>('PATCH', '/auth/me', patch)).user,
    // v7 profile photo (online-only): the client crops to a square JPEG; the server validates and serves it with access checks.
    uploadAvatar: async body => (await api<{ photo_url: string }>('PUT', '/me/avatar', undefined, { raw: { body, headers: { 'Content-Type': body.type || 'image/jpeg' } } })).photo_url,
    removeAvatar: async () => { await api('DELETE', '/me/avatar'); },
  };
}
